import React, { useRef, useState } from 'react';
import styled from '@emotion/styled';
import { designSystem } from '@/styles/theme';
import { FilterPanel, useIsMobileWidth } from '@/components/common/filter';
import { ErrorMessage, FORM_COLOR } from '@/pages/Seminar/components/form/formStyles';
import {
  MAX_SESSIONS,
  SessionInput,
  formatDateOnly,
  formatMonthDay,
  parseLocal,
  sortSessions,
  validateSessionCandidate,
} from '@/pages/Seminar/components/form/seminarFormUtils';
import PickerCalendar from '@/components/common/datePicker/PickerCalendar';
import TimeWheel from '@/components/common/datePicker/TimeWheel';
import { TimeGrid, TimeHint } from '@/components/common/datePicker/TimeSlots';
import {
  PcGrid,
  PcTimeColumn,
  PickerBody,
  PickerError,
  StepHint,
  StepPane,
  StepTabs,
} from '@/components/common/datePicker/pickerStyles';
import {
  AmPm,
  addMinutesSameDay,
  ampmOf,
  formatDayKo,
  formatTimeKo,
  initialMonth,
  isDateTimeAllowed,
  isSlotTime,
  plusTwoHours,
  splitAmPm,
  toMinutes,
} from '@/components/common/datePicker/pickerUtils';
import { ChevronLabel } from '@/components/common/elements/IconLabel';

const SessionList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SessionItem = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 52px;
  padding: 0 8px 0 16px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 15px;
  color: #1f2937;
`;

const SessionNo = styled.span`
  flex: 0 0 auto;
  min-width: 28px;
  font-weight: 700;
  color: ${designSystem.colors.primary};
`;

const SessionText = styled.span`
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RemoveButton = styled.button`
  flex: 0 0 auto;
  min-width: 44px;
  height: 40px;
  border: none;
  background: none;
  color: #6b7280;
  font-family: inherit;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    color: #dc2626;
  }
`;

const AddButton = styled.button`
  height: 52px;
  border: 1.5px dashed ${designSystem.colors.primary};
  border-radius: 12px;
  background: #fff;
  color: ${designSystem.colors.primaryDark};
  font-family: inherit;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    border-color: #d1d5db;
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

/** PC [시작 | 종료] 탭 */
const PcTabs = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  margin-bottom: 8px;
`;

const PcTab = styled.button<{ selected: boolean }>`
  min-height: 44px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 1.2;
  border: ${({ selected }) => (selected ? `2px solid ${FORM_COLOR.primary}` : `1px solid ${FORM_COLOR.border}`)};
  border-radius: 10px;
  background: ${({ selected }) => (selected ? FORM_COLOR.primaryCard : '#fff')};
  color: ${FORM_COLOR.text};
  font-family: inherit;
  font-size: 13.5px;
  font-weight: 800;
  cursor: pointer;

  small {
    font-size: 11.5px;
    font-weight: 600;
    color: ${FORM_COLOR.muted};
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

/** ① 날짜 "지난 회차와 같은 시간" 체크 */
const SameTimeRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  margin-top: 8px;
  font-size: 14.5px;
  font-weight: 700;
  color: ${FORM_COLOR.text};
  cursor: pointer;

  input {
    width: 20px;
    height: 20px;
    margin: 0;
    accent-color: ${FORM_COLOR.primary};
  }

  span {
    font-weight: 500;
    color: ${FORM_COLOR.muted};
  }
`;

/** 한 줄 요약 "10월 20일 (화) · 오후 2:00 ~ 4:00" (큰 상자 없이) */
const PlainSummary = styled.p`
  margin: 6px 2px 0;
  font-size: 15.5px;
  font-weight: 800;
  color: ${FORM_COLOR.primaryDeep};
`;

/** ② 시작 휠 아래 [종료 시간 고르기 ›] */
const NextStepButton = styled.button`
  width: 100%;
  height: 48px;
  margin-top: 10px;
  border: 1px solid ${FORM_COLOR.primary};
  border-radius: 12px;
  background: #fff;
  color: ${FORM_COLOR.primaryDeep};
  font-family: inherit;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;

  &:disabled {
    border-color: ${FORM_COLOR.border};
    color: ${FORM_COLOR.placeholder};
    cursor: default;
  }
`;

/** PC 팝업 폭 — 달력 + 시간 칸 */
const POPOVER_WIDTH_PX: number = 580;
/** 시작을 고르면 종료는 이만큼 뒤로 자동 선택 */
const AUTO_END_MINUTES: number = 120;
/** 자동 종료 상한(시간 버튼 마지막) */
const AUTO_END_LIMIT: string = '22:00';

const EMPTY_CANDIDATE: SessionInput = { date: '', startTime: '', endTime: '' };

interface SessionListFieldProps {
  sessions: SessionInput[];
  onChange: (sessions: SessionInput[]) => void;
  /** 폼 전체 검증 에러 (회차 없음 등) */
  error?: string;
}

/** 직전 회차 1주 뒤 같은 시각을 다음 입력값으로 채워 준다 (주 1회 과정이 대부분) */
const nextCandidate = (sessions: SessionInput[]): SessionInput => {
  const sorted = sortSessions(sessions);
  const last = sorted[sorted.length - 1];
  if (!last) return EMPTY_CANDIDATE;
  const lastDate = parseLocal(last.date);
  if (!lastDate) return { date: '', startTime: last.startTime, endTime: last.endTime };
  lastDate.setDate(lastDate.getDate() + 7);
  return { date: formatDateOnly(lastDate), startTime: last.startTime, endTime: last.endTime };
};

/** 시작 + 2시간(버튼 범위 오후 10:00 까지). 시작보다 뒤가 못 되면 빈칸 */
const autoEnd = (start: string, manual: boolean): string => {
  const plus: string | null = addMinutesSameDay(start, AUTO_END_MINUTES);
  if (!plus) return '';
  if (manual || toMinutes(plus) <= toMinutes(AUTO_END_LIMIT)) return plus;
  return toMinutes(AUTO_END_LIMIT) > toMinutes(start) ? AUTO_END_LIMIT : '';
};

type SessionStep = 'date' | 'start' | 'end';
const STEP_INDEX: Record<SessionStep, number> = { date: 0, start: 1, end: 2 };

/** "오후 2:00 ~ 4:00" — 종료가 같은 오전/오후면 뒤쪽 오전/오후를 뺀다 */
const formatSpan = (start: string, end: string): string =>
  ampmOf(start) === ampmOf(end) ? `${formatTimeKo(start)} ~ ${splitAmPm(end).clock}` : `${formatTimeKo(start)} ~ ${formatTimeKo(end)}`;

/**
 * 회차 목록 편집 — "N회차 추가" 칸을 누르면 폰은 ① 날짜 → ② 시작 → ③ 종료 시트(계약 6장), PC 는 왼쪽 달력 + 오른쪽 [시작 | 종료] 시간 칸.
 * 달력엔 이미 넣은 회차 날짜를 점으로, 시작을 고르면 종료는 +2시간 미리 선택(시작 이하 흐림). 두 번째 회차부터는
 * "지난 회차와 같은 시간"(기본 켬)이면 날짜만 누르고 바로 추가한다. 다른 시각은 휠로.
 * 추가할 때 지난 시각·시작≥종료·다른 회차와 겹침을 막는다(문구는 기존 그대로). 고치려면 지우고 다시 추가한다.
 */
const SessionListField: React.FC<SessionListFieldProps> = ({ sessions, onChange, error }) => {
  const mobile: boolean = useIsMobileWidth();
  const addRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [candidate, setCandidate] = useState<SessionInput>(EMPTY_CANDIDATE);
  const [step, setStep] = useState<SessionStep>('date');
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');
  /** PC 시간 칸 탭 */
  const [pcTab, setPcTab] = useState<'start' | 'end'>('start');
  const [ampm, setAmpm] = useState<AmPm>('PM');
  const [wheel, setWheel] = useState<boolean>(false);
  const [sameTime, setSameTime] = useState<boolean>(true);
  const [month, setMonth] = useState<Date>(() => initialMonth(null));
  const sorted: SessionInput[] = sortSessions(sessions);
  const isFull: boolean = sorted.length >= MAX_SESSIONS;
  const nextNo: number = sorted.length + 1;
  const hasPrevious: boolean = sorted.length > 0;
  const today: string = formatDateOnly(new Date());

  /** 시간 칸 대상(시작/종료)에 맞춰 오전/오후·휠 상태를 맞춘다 */
  const focusTarget = (time: string): void => {
    setAmpm(time ? ampmOf(time) : 'PM');
    setWheel(!!time && !isSlotTime(time));
  };

  const openEditor = (): void => {
    const next: SessionInput = nextCandidate(sorted);
    setCandidate(next);
    setStep('date');
    setDirection('forward');
    setPcTab('start');
    setSameTime(true);
    focusTarget(next.startTime);
    setMonth(initialMonth(next.date || null));
    setOpen(true);
  };

  const goStep = (target: SessionStep): void => {
    setDirection(STEP_INDEX[target] > STEP_INDEX[step] ? 'forward' : 'back');
    setStep(target);
    if (target === 'start') focusTarget(candidate.startTime);
    if (target === 'end') focusTarget(candidate.endTime);
  };

  const complete: boolean = !!candidate.date && !!candidate.startTime && !!candidate.endTime;
  const candidateError: string | null = complete ? validateSessionCandidate(candidate, sorted) : null;
  const canAdd: boolean = complete && !candidateError;
  /** ① 날짜만으로 추가(지난 회차와 같은 시간) */
  const quickAdd: boolean = hasPrevious && sameTime;

  const handleAdd = (): void => {
    if (!canAdd) return;
    onChange(sortSessions([...sorted, candidate]));
    setOpen(false);
  };

  const handleRemove = (index: number) => {
    onChange(sorted.filter((_, i) => i !== index));
  };

  /** 시작을 고르면 종료는 +2시간 미리 선택 */
  const setStart = (time: string): void => {
    setCandidate((prev) => ({ ...prev, startTime: time, endTime: time ? autoEnd(time, !isSlotTime(time)) : prev.endTime }));
  };
  const setEnd = (time: string): void => setCandidate((prev) => ({ ...prev, endTime: time }));

  const pickDate = (day: string): void => {
    setCandidate((prev) => ({ ...prev, date: day }));
    if (mobile && !quickAdd) {
      setDirection('forward');
      setStep('start');
      focusTarget(candidate.startTime);
    }
  };

  const pickStartButton = (time: string): void => {
    setStart(time);
    const end: string = autoEnd(time, false);
    if (mobile) {
      setDirection('forward');
      setStep('end');
    } else {
      setPcTab('end');
    }
    focusTarget(end);
  };

  const startDisabled = (time: string): boolean => !!candidate.date && !isDateTimeAllowed(candidate.date, time, new Date());
  const endDisabled = (time: string): boolean =>
    (!!candidate.startTime && toMinutes(time) <= toMinutes(candidate.startTime)) || startDisabled(time);

  const toggleWheel = (target: 'start' | 'end'): void => {
    const time: string = target === 'start' ? candidate.startTime : candidate.endTime;
    // 휠에서 버튼으로 돌아갈 때 버튼에 없는 시각은 비운다
    if (wheel && time && !isSlotTime(time)) (target === 'start' ? setStart : setEnd)('');
    if (wheel && time && isSlotTime(time)) setAmpm(ampmOf(time));
    setWheel(!wheel);
  };

  const calendar = (
    <PickerCalendar
      month={month}
      onMonthChange={setMonth}
      isPicked={(day: string) => day === candidate.date}
      isDisabled={(day: string) => day < today}
      onPick={pickDate}
      dots={sorted.map((session) => session.date)}
    />
  );

  /** 시작/종료 시간 칸(버튼 또는 휠) */
  const timePane = (target: 'start' | 'end', compact: boolean) => {
    const time: string = target === 'start' ? candidate.startTime : candidate.endTime;
    const label: string = `${nextNo}회차 ${target === 'start' ? '시작' : '종료'}`;
    const isDisabled = target === 'start' ? startDisabled : endDisabled;
    if (wheel) {
      return (
        <TimeWheel
          key={`${target}-wheel`}
          value={time}
          onChange={target === 'start' ? setStart : setEnd}
          isDisabled={isDisabled}
          fallback={target === 'start' ? '14:00' : plusTwoHours(candidate.startTime) ?? '16:00'}
          label={label}
        />
      );
    }
    return (
      <TimeGrid
        key={`${target}-grid`}
        ampm={ampm}
        onAmpmChange={setAmpm}
        selected={time || null}
        isDisabled={isDisabled}
        onPick={target === 'start' ? pickStartButton : setEnd}
        label={label}
        compact={compact}
        inactive={!candidate.date}
      />
    );
  };

  const summaryText: string | null = candidate.date && candidate.startTime && candidate.endTime
    ? `${formatDayKo(candidate.date)} · ${formatSpan(candidate.startTime, candidate.endTime)}`
    : null;

  // 폰 하단 버튼 — ①(같은 시간)·③ 에서만. ② 는 시간을 누르면 ③ 으로 넘어간다
  const mobileApply: boolean = step === 'end' || (step === 'date' && quickAdd);

  /** [날짜 | 시작 | 종료] 탭 — 고른 값을 보이고 누르면 그 단계로(시작 전에는 종료 탭을 못 누름) */
  const stepTabs = (
    <StepTabs
      label={`${nextNo}회차 단계`}
      current={step}
      tabs={[
        { key: 'date', label: '날짜', value: candidate.date ? formatDayKo(candidate.date) : null },
        { key: 'start', label: '시작', value: candidate.startTime ? formatTimeKo(candidate.startTime) : null, disabled: !candidate.date },
        { key: 'end', label: '종료', value: candidate.endTime ? formatTimeKo(candidate.endTime) : null, disabled: !candidate.date || !candidate.startTime },
      ]}
      onSelect={(key: string) => { if (key !== step) goStep(key as SessionStep); }}
    />
  );

  const renderMobile = () => {
    if (step === 'date') {
      return (
        <StepPane key="date" direction={direction}>
          {calendar}
          {hasPrevious && (
            <SameTimeRow>
              <input type="checkbox" checked={sameTime} onChange={(e) => setSameTime(e.target.checked)} />
              지난 회차와 같은 시간
              <span>{formatSpan(sorted[sorted.length - 1].startTime, sorted[sorted.length - 1].endTime)}</span>
            </SameTimeRow>
          )}
          {quickAdd && summaryText && <PlainSummary aria-live="polite">{summaryText}</PlainSummary>}
          {quickAdd && !candidate.date && <StepHint>날짜만 누르고 추가하면 돼요</StepHint>}
          {!quickAdd && <StepHint>날짜를 누르면 시작 시간으로 넘어가요</StepHint>}
          {quickAdd && candidateError && <PickerError role="alert">{candidateError}</PickerError>}
        </StepPane>
      );
    }
    if (step === 'start') {
      return (
        <StepPane key="start" direction={direction}>
          {timePane('start', false)}
          <TimeHint wheel={wheel} onToggle={() => toggleWheel('start')} />
          {wheel && (
            <NextStepButton type="button" disabled={!candidate.startTime} onClick={() => goStep('end')}>
              <ChevronLabel>종료 시간 고르기</ChevronLabel>
            </NextStepButton>
          )}
        </StepPane>
      );
    }
    return (
      <StepPane key="end" direction={direction}>
        {timePane('end', false)}
        <TimeHint wheel={wheel} onToggle={() => toggleWheel('end')} />
        {candidateError && <PickerError role="alert">{candidateError}</PickerError>}
      </StepPane>
    );
  };

  return (
    <>
      {sorted.length > 0 && (
        <SessionList aria-label="회차 목록">
          {sorted.map((session, index) => (
            <SessionItem key={`${session.date}-${session.startTime}`}>
              <SessionNo>{index + 1}회</SessionNo>
              <SessionText>
                {formatMonthDay(session.date)} {session.startTime} ~ {session.endTime}
              </SessionText>
              <RemoveButton
                type="button"
                aria-label={`${index + 1}회차 삭제`}
                onClick={() => handleRemove(index)}
              >
                삭제
              </RemoveButton>
            </SessionItem>
          ))}
        </SessionList>
      )}

      <AddButton
        ref={addRef}
        type="button"
        onClick={() => (open ? setOpen(false) : openEditor())}
        disabled={isFull}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {isFull ? `최대 ${MAX_SESSIONS}회까지 추가했어요` : `+ ${nextNo}회차 추가 (최대 ${MAX_SESSIONS}회)`}
      </AddButton>
      {error && <ErrorMessage>{error}</ErrorMessage>}

      <FilterPanel
        open={open}
        anchorEl={addRef.current}
        title={`${nextNo}회차 추가`}
        onClose={() => setOpen(false)}
        onApply={handleAdd}
        applyLabel={!mobile || mobileApply ? `${nextNo}회차 추가` : undefined}
        applyDisabled={!canAdd}
        popoverWidth={POPOVER_WIDTH_PX}
      >
        {mobile ? (
          <PickerBody data-no-swipe-back>
            {stepTabs}
            {renderMobile()}
          </PickerBody>
        ) : (
          <PickerBody data-no-swipe-back>
            <PcGrid>
              {calendar}
              <PcTimeColumn>
                <PcTabs role="tablist" aria-label="고를 시각">
                  {(['start', 'end'] as const).map((target) => {
                    const time: string = target === 'start' ? candidate.startTime : candidate.endTime;
                    return (
                      <PcTab
                        key={target}
                        type="button"
                        role="tab"
                        aria-selected={pcTab === target}
                        selected={pcTab === target}
                        onClick={() => {
                          setPcTab(target);
                          focusTarget(time);
                        }}
                      >
                        <small>{target === 'start' ? '시작' : '종료'}</small>
                        {time ? formatTimeKo(time) : '—'}
                      </PcTab>
                    );
                  })}
                </PcTabs>
                {wheel ? timePane(pcTab, true) : <div className="pc-time-scroll">{timePane(pcTab, true)}</div>}
                <TimeHint wheel={wheel} onToggle={() => toggleWheel(pcTab)} compact />
              </PcTimeColumn>
            </PcGrid>
            {summaryText && <PlainSummary aria-live="polite">{summaryText}</PlainSummary>}
            {candidateError && <PickerError role="alert">{candidateError}</PickerError>}
          </PickerBody>
        )}
      </FilterPanel>
    </>
  );
};

export default SessionListField;
